import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react';

import {
  CloseCircleOutline,
  CheckOutline,
  CustomAlert,
  useToast,
} from '@aslanpay/portal-common-lib';

import useRestrictAccess from 'app/hooks/useRestrictAccess';

import { RestrictAccessModalProps } from './types';

function RestrictAccessModal({
  employeeId,
  employeeName,
  isOpen,
  onClose,
  onRestrict,
}: RestrictAccessModalProps) {
  const toast = useToast();

  const { mutate: restrictAccess, isLoading: isRestrictingAccess } = useRestrictAccess({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Access restricted',
        description: `Access has been successfully restricted for ${employeeName}. Their status is now RESTRICTED.`,
      });

      onRestrict();
      onClose();
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, access could not be restricted. Please try again.',
      });
    },
  });

  const handleRestrict = () => {
    restrictAccess({ id: employeeId });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">Restrict Access</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close restrict access modal"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody mt="20px">
          <Stack spacing="20px">
            <CustomAlert
              variant="simple"
              status="warning"
              title={
                <>
                  <Text textStyle="extraSmallMedium">Are you sure?</Text>
                  <Text textStyle="extraSmallRegular">
                    This will restrict access for <strong>{employeeName}</strong> and initiate LPS
                    while keeping the leave date intact. Their status will change to RESTRICTED.
                  </Text>
                </>
              }
            />
            <Text textStyle="smallRegular" color="brand.darkGray">
              This action will:
            </Text>
            <Stack spacing="8px" pl="16px">
              <Text textStyle="smallRegular" color="brand.darkGray">
                • Block the card and restrict card access
              </Text>
              <Text textStyle="smallRegular" color="brand.darkGray">
                • Disable all modules and features
              </Text>
              <Text textStyle="smallRegular" color="brand.darkGray">
                • Change status from ACTIVE to RESTRICTED
              </Text>
              <Text textStyle="smallRegular" color="brand.darkGray">
                • Keep the leave date intact
              </Text>
            </Stack>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" size="sm" onClick={onClose} mr="12px">
            Cancel
          </Button>
          <Button
            size="sm"
            colorScheme="red"
            rightIcon={<CheckOutline boxSize="20px" />}
            isLoading={isRestrictingAccess}
            onClick={handleRestrict}
          >
            Confirm Restrict
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default RestrictAccessModal;
